import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { LinkedStatus } from './LinkedStatus'
import { LinkedButton } from './LinkedButton'
import { TwoColumn } from 'components/layout'
import workdayLogo from '../assets/workday.png'

interface WorkdaySectionProps {
  onLink: () => void
  onUnlink: () => void
  workdayProjectId: string | undefined
}

interface WorkdayRow {
  name: string
  linked: boolean
  id: number | string
}

const columnHelper = createColumnHelper<WorkdayRow>()

export const WorkdaySection: FC<WorkdaySectionProps> = ({
  onLink,
  onUnlink,
  workdayProjectId,
}) => {
  const { t } = useTranslation()

  const data: WorkdayRow[] = useMemo(() => {
    return [
      {
        name: t('features.projectsAdmin.projectDetails.workday'),
        linked: Boolean(workdayProjectId),
        id: workdayProjectId || t('common.notAvailable'),
      },
    ]
  }, [workdayProjectId, t])

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: t('features.projectsAdmin.projectDetails.workday'),
      cell: ({ getValue }) => (
        <span className="gap-3 flex items-center">
          <img src={workdayLogo} alt={t('features.projectsAdmin.projectDetails.workdayLogoAlt')} className="w-5 h-5" />
          {getValue()}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.linked, {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <LinkedStatus linked={getValue()} />,
    }),
    columnHelper.accessor('id', {
      header: t('common.id'),
    }),
    columnHelper.accessor((row) => row.linked, {
      id: 'action',
      header: t('common.action'),
      cell: ({ getValue }) => (
        <LinkedButton linked={getValue()} onLink={onLink} onUnlink={onUnlink} />
      ),
    }),
  ] as ColumnDef<WorkdayRow>[]

  return (
    <TwoColumn
      label={t('features.projectsAdmin.projectDetails.workday')}
      description={t('features.projectsAdmin.projectDetails.workdayDescription')}
    >
      <SimpleTable
        className="w-[825px]"
        columns={columns}
        data={data}
        defaultSortColumn="name"
      />
    </TwoColumn>
  )
}
